import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Download, AlertCircle, RefreshCw } from 'lucide-react';

import StaffTable from './components/StaffTable';
import StaffFilters from './components/StaffFilters';
import AddStaffModal from './components/AddStaffModal';
import AssignEventModal from './components/AssignEventModal';
import StaffDetailsModal from './components/StaffDetailsModal';
import StaffStats from './components/StaffStats';
import BulkActionsPanel from './components/BulkActionsPanel';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { useAuth } from 'contexts/AuthContext';
import staffService from 'services/staffService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const StaffManagement = () => {
  const navigate = useNavigate();
  const { userProfile, isHost, isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [events, setEvents] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalStaff: 0,
    activeStaff: 0,
    assignedStaff: 0,
    availableStaff: 0
  });
  
  // Modal states
  const [isAddStaffModalOpen, setIsAddStaffModalOpen] = useState(false);
  const [isAssignEventModalOpen, setIsAssignEventModalOpen] = useState(false);
  const [isStaffDetailsModalOpen, setIsStaffDetailsModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check permissions
  useEffect(() => {
    if (userProfile && !isHost && !isAdmin) {
      navigate('/');
    }
  }, [userProfile, isHost, isAdmin, navigate]);

  // Fetch staff data
  const fetchStaff = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let result = await staffService?.getAllStaff(userProfile);
      if (result?.error) {
        throw new Error(result.error);
      }

      setStaff(result?.data || []);
      setFilteredStaff(result?.data || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError(err?.message || 'Failed to load staff data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch events for assignments
  const fetchEvents = async () => {
    try {
      let result = await staffService?.getEventsForAssignment(userProfile);
      if (result?.error) {
        console.error('Error fetching events:', result?.error);
        return;
      }
      setEvents(result?.data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  // Fetch staff statistics
  const fetchStats = async () => {
    try {
      let result = await staffService?.getStaffStats(userProfile);
      if (result?.error) {
        console.error('Error fetching stats:', result?.error);
        return;
      }
      setStats(result?.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (userProfile && (isHost || isAdmin)) {
      fetchStaff();
      fetchEvents();
      fetchStats();
    }
  }, [userProfile, isHost, isAdmin]);

  // Handle staff actions
  const handleAddStaff = () => {
    setIsAddStaffModalOpen(true);
  };

  const handleStaffAdded = async (staffData) => {
    try {
      let result = await staffService?.createStaff(staffData, userProfile);
      if (result?.error) {
        return { success: false, error: result?.error };
      }
      
      await fetchStaff();
      await fetchStats();
      return { success: true };
    } catch (err) {
      console.error('Error adding staff:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleAssignToEvent = (staffMember) => {
    // Handle both single staff member and staff ID
    if (typeof staffMember === 'string') {
      // It's a staff ID
      setSelectedStaff([staffMember]);
    } else if (staffMember?.id) {
      // It's a staff member object
      setSelectedStaff([staffMember?.id]);
    } else {
      // Use currently selected staff
      if (selectedStaff?.length === 0) {
        setError('Please select staff members to assign');
        return;
      }
    }
    setIsAssignEventModalOpen(true);
  };

  const handleAssignmentComplete = async (assignmentData) => {
    try {
      const dataToSubmit = {
        ...assignmentData,
        staffIds: selectedStaff
      };
      
      let result = await staffService?.assignStaffToEvent(dataToSubmit, userProfile);
      if (result?.error) {
        return { success: false, error: result?.error };
      }
      
      await fetchStaff();
      await fetchStats();
      setSelectedStaff([]);
      return { success: true };
    } catch (err) {
      console.error('Error assigning staff:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleViewDetails = (staffMember) => {
    setSelectedStaffMember(staffMember);
    setIsStaffDetailsModalOpen(true);
  };

  const handleStaffUpdate = async (staffId, updates) => {
    try {
      let result = await staffService?.updateStaff(staffId, updates, userProfile);
      if (result?.error) {
        return { success: false, error: result?.error };
      }
      
      await fetchStaff();
      await fetchStats();
      return { success: true };
    } catch (err) {
      console.error('Error updating staff:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleAssignmentUpdate = async (assignmentId, updates) => {
    try {
      let result = await staffService?.updateStaffAssignment(assignmentId, updates, userProfile);
      if (result?.error) {
        return { success: false, error: result?.error };
      }
      
      await fetchStaff();
      await fetchStats();
      return { success: true };
    } catch (err) {
      console.error('Error updating assignment:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleDeleteStaff = (staffMember) => {
    setSelectedStaffMember(staffMember);
    setIsDeleteConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedStaffMember) return;

    try {
      setIsDeleting(true);
      let result = await staffService?.deleteStaff(selectedStaffMember?.id, userProfile);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }

      // Refresh data
      await fetchStaff();
      await fetchStats();
      
      // Close modal and reset state
      setIsDeleteConfirmModalOpen(false);
      setSelectedStaffMember(null);
      
      // Remove from selected staff if it was selected
      setSelectedStaff(prev => prev?.filter(id => id !== selectedStaffMember?.id));
      
    } catch (err) {
      console.error('Error deleting staff:', err);
      setError(err?.message || 'Failed to delete staff member');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStaff?.length === 0) {
      setError('Please select staff members to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedStaff?.length} staff member${selectedStaff?.length > 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      
      // Delete each staff member
      const deletePromises = selectedStaff?.map(staffId => 
        staffService?.deleteStaff(staffId, userProfile)
      );
      
      const results = await Promise.all(deletePromises);
      
      // Check for errors
      const errors = results?.filter(result => result?.error);
      if (errors?.length > 0) {
        setError(`Failed to delete ${errors?.length} staff member${errors?.length > 1 ? 's' : ''}`);
        return;
      }

      // Refresh data
      await fetchStaff();
      await fetchStats();
      setSelectedStaff([]);
      
    } catch (err) {
      console.error('Error bulk deleting staff:', err);
      setError('Failed to delete staff members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    try {
      if (selectedStaff?.length === 0) {
        setError('Please select staff members first');
        return;
      }

      let result;
      switch (action) {
        case 'activate':
          result = await staffService?.bulkUpdateStaffStatus(selectedStaff, 'active', userProfile);
          break;
        case 'deactivate':
          result = await staffService?.bulkUpdateStaffStatus(selectedStaff, 'inactive', userProfile);
          break;
        case 'suspend':
          result = await staffService?.bulkUpdateStaffStatus(selectedStaff, 'suspended', userProfile);
          break;
        case 'assign':
          handleAssignToEvent();
          return;
        case 'delete':
          handleBulkDelete();
          return;
        default:
          console.log(`Bulk ${action} for staff:`, selectedStaff);
      }

      if (result?.error) {
        setError(result?.error);
        return;
      }

      await fetchStaff();
      await fetchStats();
      setSelectedStaff([]);
    } catch (err) {
      console.error('Error with bulk action:', err);
      setError(err?.message);
    }
  };

  // Filter staff
  const handleFiltersChange = (filters) => {
    let filtered = [...staff];

    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(member =>
        member?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm) ||
        member?.user_profiles?.email?.toLowerCase()?.includes(searchTerm) ||
        member?.staff_role?.toLowerCase()?.includes(searchTerm) ||
        member?.skills?.some(skill => skill?.toLowerCase()?.includes(searchTerm))
      );
    }

    if (filters?.status && filters?.status !== 'all') {
      filtered = filtered?.filter(member => member?.status === filters?.status);
    }

    if (filters?.role && filters?.role !== 'all') {
      filtered = filtered?.filter(member => member?.staff_role === filters?.role);
    }

    if (filters?.availability && filters?.availability !== 'all') {
      if (filters?.availability === 'available') {
        filtered = filtered?.filter(member => 
          !member?.staff_event_assignments?.some(assignment => 
            assignment?.status === 'assigned' && 
            new Date(assignment?.events?.event_date) > new Date()
          )
        );
      } else if (filters?.availability === 'assigned') {
        filtered = filtered?.filter(member => 
          member?.staff_event_assignments?.some(assignment => 
            assignment?.status === 'assigned' && 
            new Date(assignment?.events?.event_date) > new Date()
          )
        );
      }
    }

    setFilteredStaff(filtered);
  };

  const handleClearFilters = () => {
    setFilteredStaff(staff);
  };

  // Handle staff table actions
  const handleStaffAction = (action, staffMember) => {
    switch (action) {
      case 'view':
        handleViewDetails(staffMember);
        break;
      case 'assign':
        handleAssignToEvent(staffMember);
        break;
      case 'edit':
        handleViewDetails(staffMember);
        break;
      case 'delete':
        handleDeleteStaff(staffMember);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!userProfile || (!isHost && !isAdmin)) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Icon name="AlertCircle" size={48} className="text-destructive mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You don't have permission to access staff management.</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Icon name="AlertCircle" size={48} className="text-destructive mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load staff data</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={fetchStaff}>
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="px-6 py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Staff Management</h1>
                <p className="text-muted-foreground mt-2">
                  Manage your event staff, assignments, and schedules
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button variant="outline">
                  <Icon name="Download" size={16} className="mr-2" />
                  Export Staff Data
                </Button>
                <Button onClick={handleAddStaff}>
                  <Icon name="UserPlus" size={16} className="mr-2" />
                  Add Staff Member
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-8">
          {/* Stats */}
          <StaffStats stats={stats} />

          {/* Filters */}
          <StaffFilters
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
          />

          {/* Bulk Actions */}
          {selectedStaff?.length > 0 && (
            <BulkActionsPanel
              selectedCount={selectedStaff?.length}
              onAction={handleBulkAction}
            />
          )}

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-foreground">
                Staff Members ({filteredStaff?.length})
              </h2>
              {selectedStaff?.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {selectedStaff?.length} selected
                </span>
              )}
            </div>
          </div>

          {/* Staff Table */}
          {filteredStaff?.length > 0 ? (
            <StaffTable
              staff={filteredStaff}
              selectedStaff={selectedStaff}
              onSelectionChange={setSelectedStaff}
              onStaffAction={handleStaffAction}
              loading={isLoading}
            />
          ) : (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No staff members found</h3>
              <p className="text-muted-foreground mb-6">
                {staff?.length === 0 
                  ? "You haven't added any staff members yet. Start by adding your first staff member." :"No staff members match your current filters. Try adjusting your search criteria."
                }
              </p>
              {staff?.length === 0 ? (
                <Button onClick={handleAddStaff}>
                  <Icon name="UserPlus" size={16} className="mr-2" />
                  Add Your First Staff Member
                </Button>
              ) : (
                <Button onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        <AddStaffModal
          isOpen={isAddStaffModalOpen}
          onClose={() => setIsAddStaffModalOpen(false)}
          onSubmit={handleStaffAdded}
          userProfile={userProfile}
        />

        <AssignEventModal
          isOpen={isAssignEventModalOpen}
          onClose={() => {
            setIsAssignEventModalOpen(false);
            setSelectedStaff([]);
          }}
          staffIds={selectedStaff}
          staff={staff}
          events={events}
          onSubmit={handleAssignmentComplete}
        />

        <StaffDetailsModal
          isOpen={isStaffDetailsModalOpen}
          onClose={() => {
            setIsStaffDetailsModalOpen(false);
            setSelectedStaffMember(null);
          }}
          staffMember={selectedStaffMember}
          staff={staff}
          events={events}
          onUpdate={handleStaffUpdate}
          onAssignmentUpdate={handleAssignmentUpdate}
          userProfile={userProfile}
        />

        <DeleteConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          onClose={() => {
            if (!isDeleting) {
              setIsDeleteConfirmModalOpen(false);
              setSelectedStaffMember(null);
            }
          }}
          onConfirm={handleConfirmDelete}
          staffMember={selectedStaffMember}
          isDeleting={isDeleting}
        />
      </div>
    </Layout>
  );
};

export default StaffManagement;